import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useLocation, useParams } from 'react-router-dom';

import SvgIcon from '@material-ui/core/SvgIcon';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import {
  getInsightsOverviewExportURL,
  useAssignmentResults,
  useMisconceptionsOverview,
  useOverallResponseOverview,
  useTrackerInstanceSectionCheckPreannotationsStatus,
  useTrackerInstanceSectionDetail,
  useTrackerInstanceSectionHasSubmittedStudentWorks
} from 'sdk';

import GradeOverview from 'pages/Teachers/Insights/components/GradeOverview';
import MisconceptionsOverview from 'pages/Teachers/Insights/components/MisconceptionsOverview';
import OverallResponseOverview from 'pages/Teachers/Insights/components/OverallResponseOverview';
import {
  GRADE_TAB,
  MISCONCEPTIONS_TAB,
  OVERALL_RESPONSE_TAB
} from 'pages/Teachers/Insights/constants';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import { useNavigation, useSections } from 'pages/Teachers/shared/hooks';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import {
  openInNewTab,
  parseParams,
  reverse,
  stringifyParams
} from 'utils/urls';

import TabSwitcher from './components/TabSwitcher';
import ExportButton from 'components/ExportButton';
import TrackerInstanceSectionContext from 'components/TrackerInstanceSectionContext';
import UserContext from 'components/UserContext';

import { ReactComponent as MainIcon } from './assets/edlightMain.svg';
import styles from './styles.module.scss';

const Insights = ({ history, width }) => {
  const user = useContext(UserContext);

  const { trackerInstanceSectionId } = useParams();
  const location = useLocation();

  const { data: trackerInstanceSection } = useTrackerInstanceSectionDetail({
    trackerInstanceSectionId
  });

  const trackerInstanceSectionData = {
    standards: trackerInstanceSection?.standards.map(
      (standard) => standard.code
    ),
    class_id: trackerInstanceSection?.section_id
  };

  const [
    latestOverallResponseMixpanelData,
    setLatestOverallResponseMixpanelData
  ] = useState(null);

  const {
    data: assignment,
    mutate: setAssignment,
    isLoading: loadingAssignment
  } = useAssignmentResults(trackerInstanceSection?.id);

  const sections = useSections(trackerInstanceSection?.tracker_id);

  const updateQueryParam = useCallback(
    (newTrackerInstanceSectionId) => {
      history.push({
        pathname: reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
          trackerInstanceSectionId: newTrackerInstanceSectionId
        }),
        search: stringifyParams(
          parseParams(location.search, {
            parseNumbers: true
          })
        )
      });
    },
    [location, history]
  );

  useNavigation({
    assignment,
    selectedSectionId: trackerInstanceSection?.id,
    sections,
    selectSection: updateQueryParam
  });

  const handleTitleClick = useCallback(() => {
    if (location.state?.from === TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL) {
      history.push(TEACHER_ASSIGNMENTS_V2_URL);
    } else {
      history.goBack();
    }
  }, [history, location.state]);

  useNavbar({
    title: '',
    handleTitleClick,
    sticky: true,
    onBackButtonClick: handleTitleClick
  });

  const exportResults = useCallback(() => {
    openInNewTab(getInsightsOverviewExportURL({ trackerInstanceSectionId }));
  }, [trackerInstanceSectionId]);

  const handleExportClick = useCallback(() => {
    const standards = trackerInstanceSection?.standards.map(
      (standard) => standard.code
    );

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'insights',
        button: 'export',
        standards,
        class_id: trackerInstanceSection?.section_id
      }
    );

    exportResults();
  }, [user, trackerInstanceSection, exportResults]);

  const params = parseParams(location.search, {
    parseNumbers: true
  });

  const defaultActiveTab = params['activeTab'];
  const defaultWorkId = params['workId'];
  const defaultTaskId = params['taskId'];
  const defaultStudentId = params['studentId'];
  const isFeedbackOpened = params['isFeedbackOpened'] === 'true';

  const [activeTab, setActiveTab] = useState(
    defaultActiveTab || OVERALL_RESPONSE_TAB
  );

  const [
    shouldFetchOverallResponseDataOneMoreTime,
    setShouldFetchOverallResponseDataOneMoreTime
  ] = useState(false);

  const [
    shouldFetchMisconceptionsDataOneMoreTime,
    setShouldFetchMisconceptionsDataOneMoreTime
  ] = useState(false);

  const {
    data: assignmentCheckPreannotationsStatus,
    isLoading: isAssignmentCheckPreannotationsStatusLoading,
    refetch: refetchAssignmentCheckPreannotationsStatus
  } = useTrackerInstanceSectionCheckPreannotationsStatus({
    trackerInstanceSectionId
  });

  const {
    data: overviewData,
    isLoading: isOverviewDataLoading,
    refetch: refetchOverview
  } = useOverallResponseOverview({
    trackerInstanceSectionId
  });

  const {
    data: misconceptionsData,
    isLoading: isMisconceptionsDataLoading,
    refetch: refetchMisconceptionsData
  } = useMisconceptionsOverview({
    trackerInstanceSectionId
  });

  const { data: hasSubmittedStudentWorks } =
    useTrackerInstanceSectionHasSubmittedStudentWorks({
      trackerInstanceSectionId
    });

  useEffect(() => {
    if (!trackerInstanceSection?.is_math_assignment) return;

    const isCheckLoading = isAssignmentCheckPreannotationsStatusLoading;
    const notFullyPreannotated =
      assignmentCheckPreannotationsStatus &&
      !assignmentCheckPreannotationsStatus.is_fully_preannotated;

    if (!isCheckLoading && notFullyPreannotated) {
      const interval = setInterval(
        refetchAssignmentCheckPreannotationsStatus,
        5000
      );
      return () => clearInterval(interval);
    } else {
      const overallResponseTimeout = setTimeout(
        () => setShouldFetchOverallResponseDataOneMoreTime(true),
        5000
      );
      const misconceptionsTimeout = setTimeout(
        () => setShouldFetchMisconceptionsDataOneMoreTime(true),
        5000
      );

      return () => {
        clearTimeout(overallResponseTimeout);
        clearTimeout(misconceptionsTimeout);
      };
    }
  }, [
    trackerInstanceSection,
    assignmentCheckPreannotationsStatus,
    refetchAssignmentCheckPreannotationsStatus,
    isAssignmentCheckPreannotationsStatusLoading
  ]);

  useEffect(() => {
    if (!trackerInstanceSection?.is_math_assignment) return;

    const shouldPoll =
      assignmentCheckPreannotationsStatus &&
      (!assignmentCheckPreannotationsStatus.is_fully_preannotated ||
        shouldFetchOverallResponseDataOneMoreTime) &&
      activeTab === OVERALL_RESPONSE_TAB;

    if (shouldPoll) {
      const interval = setInterval(() => {
        refetchOverview();
        if (shouldFetchOverallResponseDataOneMoreTime) {
          setShouldFetchOverallResponseDataOneMoreTime(false);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [
    trackerInstanceSection,
    assignmentCheckPreannotationsStatus,
    refetchOverview,
    shouldFetchOverallResponseDataOneMoreTime,
    activeTab
  ]);

  useEffect(() => {
    if (!trackerInstanceSection?.is_math_assignment) return;

    const shouldPoll =
      assignmentCheckPreannotationsStatus &&
      (!assignmentCheckPreannotationsStatus.is_fully_preannotated ||
        shouldFetchMisconceptionsDataOneMoreTime) &&
      activeTab === MISCONCEPTIONS_TAB;

    if (shouldPoll) {
      const interval = setInterval(() => {
        refetchMisconceptionsData();
        if (shouldFetchMisconceptionsDataOneMoreTime) {
          setShouldFetchMisconceptionsDataOneMoreTime(false);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [
    trackerInstanceSection,
    assignmentCheckPreannotationsStatus,
    refetchMisconceptionsData,
    shouldFetchMisconceptionsDataOneMoreTime,
    activeTab
  ]);

  const handleTabSwitch = useCallback(
    (tab) => {
      if (tab === OVERALL_RESPONSE_TAB) {
        refetchOverview();
      } else {
        refetchMisconceptionsData();
      }
      setActiveTab(tab);
    },
    [refetchMisconceptionsData, refetchOverview]
  );

  const fullyPreAnnotated = useMemo(() => {
    return (
      assignmentCheckPreannotationsStatus &&
      assignmentCheckPreannotationsStatus.is_fully_preannotated
    );
  }, [assignmentCheckPreannotationsStatus]);

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          {trackerInstanceSection?.standards.map((standard, index) => (
            <div key={index} className={styles.standardsContainer}>
              <SvgIcon component={MainIcon} />
              <span>
                {standard['code']} - {standard['short_description']}
              </span>
            </div>
          ))}
        </div>
        <div className={styles.headerActions}>
          <div className={styles.buttonContainer}>
            <ExportButton handleClick={handleExportClick} />
          </div>
          {trackerInstanceSection?.is_math_assignment ? (
            <button
              className={styles.insightsButton}
              onClick={() => {
                history.push({
                  pathname: reverse(TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL, {
                    trackerInstanceSectionId
                  })
                });
              }}
            >
              <span>Switch to new Live Classroom experience</span>
              <ArrowBackIcon />
            </button>
          ) : (
            <></>
          )}
        </div>
      </div>

      {trackerInstanceSection?.is_math_assignment && (
        <TabSwitcher activeTab={activeTab} onTabSwitch={handleTabSwitch} />
      )}

      <TrackerInstanceSectionContext.Provider
        value={trackerInstanceSectionData}
      >
        <div className={styles.detailContainer}>
          {trackerInstanceSection?.is_math_assignment && (
            <>
              {activeTab === OVERALL_RESPONSE_TAB && (
                <OverallResponseOverview
                  data={overviewData}
                  isLoading={isOverviewDataLoading}
                  refetchOverview={refetchOverview}
                  trackerInstanceSectionId={trackerInstanceSectionId}
                  setLatestOverallResponseMixpanelData={
                    setLatestOverallResponseMixpanelData
                  }
                  latestOverallResponseMixpanelData={
                    latestOverallResponseMixpanelData
                  }
                  fullyPreAnnotated={fullyPreAnnotated}
                  hasSubmittedStudentWorks={hasSubmittedStudentWorks}
                  onPreannotationsRetrySuccess={
                    refetchAssignmentCheckPreannotationsStatus
                  }
                />
              )}
              {activeTab === MISCONCEPTIONS_TAB && (
                <MisconceptionsOverview
                  data={misconceptionsData}
                  isLoading={isMisconceptionsDataLoading}
                  refetchOverview={refetchMisconceptionsData}
                  fullyPreAnnotated={fullyPreAnnotated}
                  hasSubmittedStudentWorks={hasSubmittedStudentWorks}
                />
              )}
            </>
          )}
          {(activeTab === GRADE_TAB ||
            !trackerInstanceSection?.is_math_assignment) &&
            assignment && (
              <GradeOverview
                trackerId={trackerInstanceSection?.tracker_id}
                width={width}
                assignment={assignment}
                setAssignment={setAssignment}
                loadingAssignment={loadingAssignment}
                studentDeepLink={trackerInstanceSection?.student_deep_link}
                selectedSectionId={trackerInstanceSection?.id}
                defaultStudentId={defaultStudentId}
                defaultWorkId={defaultWorkId}
                defaultTaskId={defaultTaskId}
                isFeedbackOpened={isFeedbackOpened}
              />
            )}
        </div>
      </TrackerInstanceSectionContext.Provider>
    </div>
  );
};

export default Insights;
